import React, { useState, useRef, useCallback, useEffect } from 'react';
import i18next from 'i18next';

import { debounce } from 'lodash';

import { OverlayPanel } from 'primereact/overlaypanel';
import { ProgressSpinner } from 'primereact/progressspinner';

import axiosInstance from '../../config/axiosConfig';
import useSelectedCompanyStore from '../../store/useSelectedCompanyStore';

const t = (i18nKey, params) => i18next.t(`userDashboard.header.${i18nKey}`, params);

const GeneralQuery = ({ onResults }) => {
    const [searchQuery, setSearchQuery] = useState('');
    const [loading, setLoading] = useState(false);
    const [awaitingForInput, setAwaitingForInput] = useState(false);
    const [searchResults, setSearchResults] = useState({ campaigns: [], candidates: [] });
    const searchPanelRef = useRef(null);

    const selectedCompany = useSelectedCompanyStore((state) => state.selectedCompany);

    const fetchSearchResults = async (query) => {
        if (query.length < 3) return;
        setLoading(true);

        try {
            const { data } = await axiosInstance.get(
                `/dashboard/query/${selectedCompany?.company_id}/generalQuery`,
                {
                    params: { query },
                }
            );

            setSearchResults(data);
            onResults && onResults(data);
        } catch (error) {
            console.error('Error fetching search results:', error);
        } finally {
            setLoading(false);
            setAwaitingForInput(false);
        }
    };

    const debouncedFetchData = useCallback(
        debounce((query) => {
            fetchSearchResults(query);
        }, 1500),
        []
    );

    useEffect(() => {
        if (searchQuery.length >= 3) {
            debouncedFetchData(searchQuery);
        } else {
            setSearchResults({ campaigns: [], candidates: [] });
        }
    }, [searchQuery, debouncedFetchData]);

    const handleInputChange = (e) => {
        setAwaitingForInput(true);

        const value = e.target.value;
        setSearchQuery(value);
    };


    const getBestCategory = () => {
        const campaignCount = searchResults.campaigns?.length || 0;
        const candidateCount = searchResults.candidates?.length || 0;
        return campaignCount >= candidateCount ? 'Campaigns' : 'Candidates';
    };

    const bestCategory = getBestCategory();

    const handleCampaignClick = (campaignId) => {
        window.location.href = `/dashboard/campaigns/${campaignId}/candidates`;
    };

    const handleCandidateClick = (candidate) => {
        window.location.href = `/dashboard/campaigns/${candidate.campaign_id}/candidates/${candidate.candidate_id}`;
    };

    const renderCampaigns = () => {
        if (!searchResults.campaigns.length) return null;

        return (
            <div className="dropdown-category">
                <h4
                    className={`title is-6 has-text-grey-dark category-header ${bestCategory === 'Campaigns' ? 'is-best-category' : ''}`}
                >
                    <i className="fa-solid fa-bullhorn fa-lg mr-2"></i>
                    Campagnes ({searchResults.campaigns.length})
                </h4>

                {searchResults.campaigns.map((campaign) => (
                    <div
                        key={campaign.campaign_id}
                        className="g-query-dropdown-item g-query-campaign-item is-clickable"
                        onClick={() => handleCampaignClick(campaign.campaign_id)}
                    >
                        <div className="columns is-mobile is-vcentered">
                            <div className="column is-4">
                                <strong>{campaign.campaign_name}</strong>
                            </div>
                            <div className="column is-4">
                                <span className="is-size-7 has-text-grey">
                                    Participants {campaign?.candidates_count || 0}
                                </span>
                            </div>
                            <div className="column is-4">
                                <span className="is-size-7 has-text-grey">
                                    Crée le {new Date(campaign.created_at).toLocaleDateString()}
                                </span>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        );
    };

    const renderCandidates = () => {
        if (!searchResults.candidates.length) return null;

        return (
            <div className="dropdown-category">
                <h4
                    className={`title is-6 has-text-grey-dark category-header ${bestCategory === 'Candidates' ? 'is-best-category' : ''
                        }`}
                >
                    <i className="fa-solid fa-user fa-lg mr-2"></i>
                    Participants ({searchResults.candidates.length})
                </h4>

                {searchResults.candidates.map((candidate) => (
                    <div
                        key={candidate.candidate_id}
                        className="g-query-dropdown-item candidate-item is-clickable"
                        onClick={() => handleCandidateClick(candidate)}
                    >
                        <div className="columns is-mobile is-vcentered">
                            <div className="column is-4">
                                <strong>
                                    {candidate.first_name} {candidate.last_name}
                                </strong>
                            </div>
                            <div className="column is-4">
                                <span className="is-size-7 has-text-grey">
                                    Tests réalisés {candidate.choosen_tests?.length || 0}
                                </span>
                            </div>
                            <div className="column is-4">
                                <span className="is-size-7 has-text-grey">
                                    Crée le {new Date(candidate.creation_date).toLocaleDateString()}
                                </span>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        );
    };

    const renderSearchResults = () => {
        const { campaigns, candidates } = searchResults;

        if (loading) {
            return <ProgressSpinner style={{ width: '25px', height: '25px' }} strokeWidth="4" />
        }

        if (!searchQuery || searchQuery?.length < 3) {
            return <p className="no-results">Veuillez insérer au moins trois lettres</p>;
        }

        if (awaitingForInput) {
            return <p className="no-results">En attente...</p>;
        }


        if (campaigns.length === 0 && candidates.length === 0) {
            return <p className="no-results">Aucun résultat trouvé</p>;
        }

        return (
            <div className="search-dropdown">
                {renderCampaigns()}
                {renderCandidates()}
            </div>
        );
    };

    return (
        <div className="search-bar">
            <div className="field">
                <p className="control has-icons-left">
                    <input
                        className="input"
                        type="text"
                        placeholder={t('inputPlaceholder')}
                        value={searchQuery}
                        onChange={handleInputChange}
                        onFocus={(e) => searchPanelRef.current.show(e)}
                    />
                    <span className="icon is-small is-left">
                        <i className="fa-solid fa-magnifying-glass"></i>
                    </span>
                </p>
            </div>
            <OverlayPanel ref={searchPanelRef} className="g-query-overlay">
                {renderSearchResults()}
            </OverlayPanel>
        </div>
    );
};

export default GeneralQuery;